/**
 * Turn a saved conversation (`GET /chat/conversation/{id}`) into chat bubbles.
 *
 * The backend attaches each answer's own sources to its message, matched by the
 * turn that saved them. Reopening a conversation used to match sources to
 * answers here, by the question's text and then by position, and both broke on
 * ordinary conversations: a stopped Explain and its retry asked the same
 * question, so the first answer took the second's sources and the second showed
 * no citations and no Sources button (staging QA, September 2026). The old
 * matching stays only for a backend that predates per-message sources.
 *
 * A stopped turn reads the way it did live: its question, then the part of the
 * answer that was on screen, or the stop notice when none was.
 * Identical in both clients (`src/services/conversationHistory.ts`).
 */

export interface SavedMessage {
  role: string;
  content?: string | null;
  turn_id?: string;
  status?: string;
  sources?: unknown[];
}

export interface SavedSourceLog {
  sources?: unknown[];
  user_query?: string;
}

export interface HistoryBubble {
  sender: 'user' | 'bot';
  text: string;
  sources?: any[];
  /** A stopped turn with no answer text: the bubble is the stop notice. */
  noAnswer?: boolean;
}

export const TURN_STATUS_STOPPED = 'stopped';

/** The roles the history renders as VoteMate's side of the conversation. */
export function isBotRole(role: string): boolean {
  return (
    role === 'bot' || role === 'assistant' || role === 'orchestrator' || role.includes('Agent')
  );
}

/** The pre-per-message matching, for a backend that sends only `source_logs`. */
function legacySourceMatcher(sourceLogs: SavedSourceLog[]) {
  const byQuery = new Map<string, unknown[]>();
  for (const log of sourceLogs) {
    if (log.user_query && Array.isArray(log.sources) && log.sources.length > 0) {
      byQuery.set(log.user_query.toLowerCase().trim(), log.sources);
    }
  }
  return (botIndex: number, userQuery: string): unknown[] | undefined => {
    const byText = byQuery.get(userQuery);
    if (byText && byText.length > 0) {
      byQuery.delete(userQuery);
      return byText;
    }
    const log = sourceLogs[botIndex];
    return log && Array.isArray(log.sources) && log.sources.length > 0 ? log.sources : undefined;
  };
}

/**
 * @param messages The conversation's saved messages, in order.
 * @param sourceLogs The response's `source_logs` (used only when messages carry
 *   no `sources` of their own).
 * @param stoppedText The localized stop notice ("Stopped at your request.").
 */
export function historyBubbles(
  messages: SavedMessage[],
  sourceLogs: SavedSourceLog[],
  stoppedText: string
): HistoryBubble[] {
  const legacySources = legacySourceMatcher(Array.isArray(sourceLogs) ? sourceLogs : []);
  const bubbles: HistoryBubble[] = [];
  let lastUserQuery = '';
  let botIndex = 0;

  messages.forEach((message, index) => {
    if (!message || typeof message.role !== 'string') return;
    if (message.role === 'user') {
      const text = message.content ?? '';
      lastUserQuery = text.toLowerCase().trim();
      bubbles.push({ sender: 'user', text });
      if (message.status === TURN_STATUS_STOPPED) {
        const next = messages[index + 1];
        const answerShown =
          next !== undefined &&
          typeof next.role === 'string' &&
          isBotRole(next.role) &&
          next.turn_id === message.turn_id;
        if (!answerShown) bubbles.push({ sender: 'bot', text: stoppedText, noAnswer: true });
      }
      return;
    }
    if (!isBotRole(message.role)) return;
    const sources = Array.isArray(message.sources)
      ? message.sources
      : legacySources(botIndex, lastUserQuery);
    botIndex += 1;
    const bubble: HistoryBubble = { sender: 'bot', text: message.content ?? '' };
    if (sources && sources.length > 0) bubble.sources = sources as any[];
    bubbles.push(bubble);
  });
  return bubbles;
}
